import { Check, LayoutGrid } from 'lucide-react';
import { useAppConfig } from '@shared/app/app-config';
import { useAuthStore } from '@shared/store/auth.store';
import { switchableApplications } from '@shared/config/applications';
import { openApplication } from '@shared/auth/open-application';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { cn } from '@shared/lib/utils';

/**
 * HEXAUNITY application switcher.
 *
 * Lists exactly the applications this account holds among HRMS and Finance
 * (`switchableApplications`, from `/auth/me` → applications — the data the
 * backend's ApplicationAccessGuard enforces on every request). HRMS only shows
 * HRMS, Finance only shows Finance, both show both, neither shows nothing.
 * The Employee Portal is not offered for now (see
 * `SWITCHABLE_APPLICATION_CODES`). Hiding an entry is not the protection: the
 * target application's guard and every backend request still check.
 *
 * Switching keeps the Identity session: the target application opens in the
 * SAME tab with the session id in the URL fragment and restores the session
 * from the existing HttpOnly refresh cookie — no second sign-in. Same tab, so
 * two applications never rotate the same refresh token at once
 * (identity-service would treat that as replay).
 */
export function AppSwitcher() {
  const { application } = useAppConfig();
  const applications = useAuthStore((state) => state.authorization.applications);

  const available = switchableApplications(applications);
  if (available.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Switch application"
        >
          <LayoutGrid className="size-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          HEXAUNITY
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {available.map((definition) => {
          const current = definition.code === application;
          const disabled = !current && !definition.url;
          return (
            <DropdownMenuItem
              key={definition.code}
              disabled={disabled}
              className="gap-3 py-2"
              onSelect={(event) => {
                if (current || !definition.url) {
                  event.preventDefault();
                  return;
                }
                void openApplication(definition.url);
              }}
            >
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-md',
                  current ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                )}
              >
                <definition.icon className="size-4" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{definition.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {disabled ? 'Address not configured' : definition.description}
                </span>
              </span>
              {current ? <Check className="size-4 shrink-0 text-primary" aria-hidden="true" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
